package info.rezident.app.data.user

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.remoteconfig.get
import dev.gitlive.firebase.remoteconfig.remoteConfig
import info.rezident.app.core.LocalStorage
import info.rezident.app.core.Logger
import info.rezident.app.core.isLocalEnvironment
import info.rezident.app.data.PriceVariant
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.firstOrNull
import kotlinx.coroutines.launch
import kotlinx.coroutines.withTimeout
import org.w3c.dom.url.URLSearchParams
import kotlin.time.Duration.Companion.seconds

private const val PRICE_VARIANT = "price_variant"
private const val RECOMMENDATIONS_SORTED = "recommendations_sorted"

// Upper bound for the startup fetch, so the variants always resolve even when the network hangs
private val FETCH_TIMEOUT = 30.seconds

object VariantsRepository {
    private val scope = CoroutineScope(Dispatchers.Default)
    private val config = Firebase.remoteConfig
    private val _priceVariant = MutableStateFlow<PriceVariant?>(null)
    private val _recommendationsSorted = MutableStateFlow<Boolean?>(null)

    suspend fun priceVariant() = _priceVariant.firstOrNull { it != null } ?: PriceVariant.default

    /**
     * A/B test for the recommendations page: true = variant A (ranked, previous behavior),
     * false = variant B (ranking hidden). Resolved once at app startup, so this only waits when it's
     * called before the startup fetch has finished (at most [FETCH_TIMEOUT] after the site loaded).
     */
    suspend fun recommendationsSorted(): Boolean = _recommendationsSorted.filterNotNull().first()

    /**
     * Analytics label of the recommendations variant: "A" (ranked) or "B" (ranking hidden).
     */
    fun recommendationsVariantLabel(sorted: Boolean): String = if (sorted) "A" else "B"

    /**
     * Same label as [recommendationsVariantLabel], or null until the startup fetch has resolved the variant.
     * Doesn't wait, so analytics events fired outside the recommendations load flow (checkout, purchase)
     * are never delayed or lost because of it.
     */
    fun recommendationsVariantLabelOrNull(): String? =
        _recommendationsSorted.value?.let(::recommendationsVariantLabel)

    fun initRemoteConfig() {
        scope.launch {
            config.apply {
                // Set a minimum fetch interval to avoid frequent requests.
                settings {
                    minimumFetchInterval = 3600.seconds
                }

                // Set a default value to ensure the app works before fetching remote values.
                setDefaults(
                    PRICE_VARIANT to PriceVariant.default.name,
                    RECOMMENDATIONS_SORTED to true
                )
                // Fetch the latest config from the server and activate it. A failed or timed-out fetch
                // (e.g. offline) keeps the last activated values, or the defaults, so the variants always resolve.
                runCatching { withTimeout(FETCH_TIMEOUT) { fetchAndActivate() } }
                    .onFailure { Logger.warn("Remote Config fetch failed, using cached values or defaults: ${it.message}") }
                // Ensure the config is initialized before use.
                runCatching { ensureInitialized() }
                    .onFailure { Logger.warn("Remote Config initialization failed: ${it.message}") }
            }

            // Resolved first and on its own, so a failure in the price variant below can't leave it unset.
            // Not stored locally: Remote Config keeps the user's percentile stable, and a local copy
            // would keep users on their variant after the winner is rolled out.
            _recommendationsSorted.value = localRecommendationsSortedOverride()
                ?: runCatching { config.get<String>(RECOMMENDATIONS_SORTED) }.getOrNull()?.toBooleanStrictOrNull()
                ?: true

            // Check for a locally stored variant to ensure persistence across sessions.
            val savedVariant = LocalStorage.get(PRICE_VARIANT)
            // Use the local variant if available, otherwise use the remote one and save it.
            val variant = savedVariant ?: config.get<String>(PRICE_VARIANT).also {
                LocalStorage.save(PRICE_VARIANT, it)
            }

            // Update the price variant state flow with the determined value.
            _priceVariant.value = PriceVariant.valueOf(variant)
        }
    }

    /**
     * Local testing only: `?recommendations_sorted=true|false` forces a variant on localhost/preview hosts.
     */
    private fun localRecommendationsSortedOverride(): Boolean? =
        if (isLocalEnvironment()) {
            URLSearchParams(window.location.search).get(RECOMMENDATIONS_SORTED)?.toBooleanStrictOrNull()
        } else {
            null
        }
}
